import type Phaser from 'phaser';
import { ELEVATOR_CABIN_SPOTS, EventBus, elevatorSpawnName, parseElevatorProps, sortElevatorFloors, useGameStore, type ElevatorFloor } from '@campus/core';
import { ElevatorDoor } from '../sprites/ElevatorDoor.js';
import { API_BASE } from '../../lib/apiBase.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { canEnterZoneHere } from '../../lib/access.js';

const TILE = 32;
/** Arrivée : les pieds de l'avatar dans la rangée devant l'ascenseur. */
const ARRIVAL_BELOW_PX = 20;
/** Pieds de l'avatar dans la cabine : sur la rangée du seuil, sous le bas du sprite. */
const CABIN_ABOVE_BOTTOM_PX = 6;

/** Une place dans la cabine d'un ascenseur : où se tient l'avatar dedans, et ses portes. */
export interface ElevatorCabin {
  x: number;
  y: number;
  door: ElevatorDoor;
}

interface ElevatorZone {
  id: string;
  group: string;
  floor: number;
  sprite: ElevatorDoor;
  /** Rangée devant l'ascenseur (px) : s'y arrêter ouvre la liste des étages. */
  trigger: { x: number; y: number; width: number; height: number };
  /** Entrées et sorties en cours (avatar local ou des autres) : portes ouvertes tant qu'il en reste. */
  holds: number;
  /** Avatar local arrêté devant, liste des étages ouverte. */
  localOpen: boolean;
}

/**
 * Ascenseurs d'une carte (zones `elevator`, `@campus/core/elevators`) :
 * sprites animés, liste des étages du groupe (`GET /world/elevators/:group`),
 * fenêtre ElevatorPanel quand l'avatar est devant, départ vers l'étage choisi.
 * Les portes s'ouvrent pour tout le monde (`hold`/`release`) : la scène joue
 * les entrées et sorties des autres, relayées par le temps réel.
 */
export class Elevators {
  private zones: ElevatorZone[] = [];
  private floorsByGroup = new Map<string, ElevatorFloor[]>();
  private current: ElevatorZone | null = null;
  /** Ascenseur dont la liste ne s'ouvre pas tant qu'on n'est pas reparti (arrivée, Échap). */
  private suppressedId: string | null = null;
  private departing = false;
  private readonly onChoose = ({ mapSlug }: { mapSlug: string }) => this.choose(mapSlug);
  private readonly onDismiss = () => this.dismiss();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly mapSlug: string,
    private readonly depthOf: (bottomY: number) => number,
    /** Étage choisi : la scène demande une place dans la cabine, y fait entrer l'avatar puis change de carte. */
    private readonly depart: (mapSlug: string, spawnName: string, zoneId: string, direction: 'up' | 'down') => void,
  ) {
    EventBus.on('ui:elevator-choose', this.onChoose);
    EventBus.on('ui:elevator-dismiss', this.onDismiss);
  }

  /** Zones de la carte (rejouable) ; renvoie les points d'arrivée devant chaque ascenseur. */
  setZones(zones: Array<{ id: string; type: string; properties: Record<string, unknown>; x: number; y: number; width: number; height: number }>): Map<string, { x: number; y: number }> {
    for (const z of this.zones) z.sprite.destroy();
    this.zones = [];
    this.current = null;
    const spawns = new Map<string, { x: number; y: number }>();
    for (const z of zones) {
      if (z.type !== 'elevator') continue;
      const props = parseElevatorProps(z.properties);
      if (!props) continue;
      const left = z.x * TILE;
      const bottom = (z.y + z.height) * TILE;
      const width = z.width * TILE;
      const sprite = new ElevatorDoor(this.scene, left + (width - 2 * TILE) / 2, bottom);
      // Juste devant le mur qu'il recouvre (même tri en Y que les murs et les portes).
      sprite.setDepth(this.depthOf(bottom) + 0.01);
      this.scene.add.existing(sprite);
      this.zones.push({ id: z.id, group: props.group, floor: props.floor, sprite, trigger: { x: left, y: bottom, width, height: TILE }, holds: 0, localOpen: false });
      spawns.set(elevatorSpawnName(props.group), { x: left + width / 2, y: bottom + ARRIVAL_BELOW_PX });
      void this.loadFloors(props.group);
    }
    return spawns;
  }

  /** Arrivée par l'ascenseur : son id (la scène fait sortir l'avatar), pas de liste avant d'être reparti. */
  arrive(spawnName: string | null): string | null {
    const zone = this.zones.find((z) => elevatorSpawnName(z.group) === spawnName);
    if (!zone) return null;
    this.suppressedId = zone.id;
    return zone.id;
  }

  /** Place `spot` (ELEVATOR_CABIN_SPOTS) dans la cabine de cet ascenseur, null s'il n'est pas sur la carte. */
  cabin(zoneId: string, spot = 0): ElevatorCabin | null {
    const zone = this.zones.find((z) => z.id === zoneId);
    if (!zone) return null;
    const offset = ELEVATOR_CABIN_SPOTS[spot] ?? ELEVATOR_CABIN_SPOTS[0]!;
    return {
      x: zone.trigger.x + zone.trigger.width / 2 + offset.dx,
      y: zone.trigger.y - CABIN_ABOVE_BOTTOM_PX + offset.dy,
      door: zone.sprite,
    };
  }

  /** Quelqu'un entre ou sort : portes ouvertes jusqu'au `release` correspondant. */
  hold(zoneId: string): void {
    const zone = this.zones.find((z) => z.id === zoneId);
    if (!zone) return;
    zone.holds++;
    this.refreshDoor(zone);
  }

  /** Fin d'une entrée ou d'une sortie ; `direction` allume la flèche si les portes se ferment. */
  release(zoneId: string, direction?: 'up' | 'down'): void {
    const zone = this.zones.find((z) => z.id === zoneId);
    if (!zone) return;
    zone.holds = Math.max(0, zone.holds - 1);
    this.refreshDoor(zone, direction);
  }

  /** Pieds de l'avatar (px) ; appelé à chaque image. */
  update(x: number, y: number): void {
    if (this.departing) return;
    const zone = this.zones.find((z) => x >= z.trigger.x && x <= z.trigger.x + z.trigger.width && y >= z.trigger.y && y <= z.trigger.y + z.trigger.height) ?? null;
    if (zone === this.current) return;
    if (this.current) {
      this.current.localOpen = false;
      this.refreshDoor(this.current);
      EventBus.emit('elevator:closed', {});
    }
    this.current = zone;
    if (!zone) { this.suppressedId = null; return; }
    if (zone.id === this.suppressedId) return;
    zone.localOpen = true;
    this.refreshDoor(zone);
    this.showFloors(zone);
  }

  destroy(): void {
    EventBus.off('ui:elevator-choose', this.onChoose);
    EventBus.off('ui:elevator-dismiss', this.onDismiss);
    for (const z of this.zones) z.sprite.destroy();
    this.zones = [];
    if (this.current) EventBus.emit('elevator:closed', {});
    this.current = null;
  }

  private refreshDoor(zone: ElevatorZone, direction?: 'up' | 'down'): void {
    if (zone.holds > 0 || zone.localOpen) zone.sprite.open();
    else zone.sprite.close(direction);
  }

  private showFloors(zone: ElevatorZone): void {
    const floors = this.floorsByGroup.get(zone.group);
    if (!floors || floors.length < 2) return; // pas encore chargés (re-affiché à l'arrivée), ou seul étage
    EventBus.emit('elevator:opened', { currentSlug: this.mapSlug, floors });
  }

  private async loadFloors(group: string): Promise<void> {
    if (this.floorsByGroup.has(group)) return;
    const jwt = useGameStore.getState().jwt;
    if (!jwt) return;
    try {
      const res = await fetch(`${API_BASE}/world/elevators/${encodeURIComponent(group)}`, {
        headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': getTenantSlug() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { floors } = await res.json() as { floors: ElevatorFloor[] };
      this.floorsByGroup.set(group, sortElevatorFloors(floors));
      if (this.current?.group === group && this.current.id !== this.suppressedId) this.showFloors(this.current);
    } catch (err) {
      console.warn(`[Elevators] étages de « ${group} » introuvables :`, err);
    }
  }

  private choose(mapSlug: string): void {
    const zone = this.current;
    if (!zone || this.departing || mapSlug === this.mapSlug) return;
    const target = this.floorsByGroup.get(zone.group)?.find((f) => f.mapSlug === mapSlug);
    if (!target) return;
    if (!canEnterZoneHere(mapSlug)) {
      EventBus.emit('ui:toast', { message: 'Réservé : tu n\'as pas accès à cet étage.', tone: 'info' });
      return;
    }
    this.departing = true;
    EventBus.emit('elevator:closed', {});
    // La scène tient les portes ouvertes pendant l'entrée (hold), la liste n'y est plus pour rien.
    this.depart(mapSlug, elevatorSpawnName(zone.group), zone.id, target.floor > zone.floor ? 'up' : 'down');
    zone.localOpen = false;
    this.refreshDoor(zone);
  }

  private dismiss(): void {
    if (!this.current) return;
    this.suppressedId = this.current.id;
    this.current.localOpen = false;
    this.refreshDoor(this.current);
    EventBus.emit('elevator:closed', {});
  }
}
